import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, RadioGroup, CheckboxGroup, FileField, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection, makeUploader,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection,
} from "@/components/FormShell";

const SERVICE_TO = ["Students", "Staff", "HOD", "Managers", "Parents", "Guest",
  "Senior Management", "New Parents Prospects", "All"];
const ITEMS = ["Breakfast", "Morning Snacks", "Lunch", "Evening Snacks",
  "Hi-Tea", "Dinner", "Other"];
const CAKE_TO = ["Student", "Staff", "Guest"];

export default function NewFnB() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  const [form, setForm] = useState({
    staffName: user?.name || "", staffEmail: user?.email || "", staffContact: "",
    mainDept: "", subDept: "", hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",
    // Phase 9 — Requirement Type routing
    requirement_type: "",
    // Cake fields (only used when requirement_type=="cake")
    cake_to_whom: "", cake_student_name: "", cake_student_grade: "",
    cake_student_gr_number: "", cake_student_day_boarding: "",
    cake_weight: "", cake_flavor: "", cake_message: "",
    cutleries_required_cake: "No",
    cake_required_date: "", cake_required_time: "", cake_required_location: "",
    // III
    purposeOfRequirement: "", peopleCount: "",
    requirementDate: "", requirementTime: "", location: "",
    students_included: "No", student_type: "",
    day_school_count: "", boarding_count: "", total_students_count: "",
    // IV
    serviceTo: [], itemsSelected: [], otherItem: "",
    cakeRequired: "No", cakeTo: "", cakeConsent: "No",
    // V
    cookingInstructions: "", allergies: "",
    // VI
    cutleriesRequired: "No", servingStaffRequired: "No",
    is_emergency: false, emergency_reason: "",
    // Payment (Phase 9)
    payment_paid_by: "", payment_mode: "", parent_email: "",
    // Consent (Phase 9) — must be pre-initialized to avoid runtime error
    consents: { applicantResponsibleInfo: false },
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onUpload = makeUploader(api, setAttachments, setUploading, toast, formatApiError);

  const validate = () => {
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    // Phase 9 — Requirement Type is mandatory
    if (!form.requirement_type) {
      toast.error("Please select Requirement Type (Cake or Food & Beverages)."); return false;
    }
    const isCake = form.requirement_type === "cake";
    const commonReq = ["staffName", "staffEmail", "staffContact", "mainDept", "subDept"];
    const fnbReq = ["purposeOfRequirement", "peopleCount", "requirementDate", "requirementTime", "location"];
    const cakeReq = ["cake_to_whom", "cake_weight", "cake_flavor",
                       "cake_required_date", "cake_required_time", "cake_required_location"];
    for (const k of [...commonReq, ...(isCake ? cakeReq : fnbReq)]) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill: ${k}`); return false;
      }
    }
    if (isCake && form.cake_to_whom === "Student") {
      for (const k of ["cake_student_name", "cake_student_grade",
                        "cake_student_gr_number", "cake_student_day_boarding"]) {
        if (!String(form[k] || "").trim()) { toast.error(`Please fill: ${k}`); return false; }
      }
    }
    if (!isCake) {
      if (form.serviceTo.length === 0) { toast.error("Select at least one category."); return false; }
      if (form.itemsSelected.length === 0) { toast.error("Select at least one item."); return false; }
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    // Phase 9 — consent mandatory
    if (!form.consents?.applicantResponsibleInfo) {
      toast.error("Please tick the applicant-responsibility consent.");
      return false;
    }
    // Phase 9 — parent email required when payment_paid_by == parent
    const pby = String(form.payment_paid_by || "").toLowerCase();
    if (pby === "parent" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(form.parent_email || "").trim())) {
      toast.error("Parent email is required when Payment Paid By is Parents.");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/requisitions/fnb", {
        form_type: "fnb",
        parent_ref_code: parentRefCode || undefined,
        form_data: { ...stripSelfSkipTransients(form), attachments },
      });
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#c8632a] font-semibold">
          VGWS-FNB-2026 · Cake / Food & Beverages Requisition
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Cake / Food &amp; Beverages Requisition Form
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">
          Vishwashanti Gurukul World School · Rajbaug, Loni Kalbhor
        </p>

        <TopNotice>
          <div className="font-serif font-semibold mb-1 flex items-center gap-2">
            <AlertCircle size={16} /> Important
          </div>
          {form.requirement_type === "cake" && (
            <p className="mb-2 rounded-md border-l-4 border-[#b3261e] bg-[#fcebe9] px-3 py-2 text-[#b3261e] font-semibold"
               data-testid="cake-red-note">
              For cake requirement, ideal timeline to submit this form is at least 3 working days prior to the requirement date.
            </p>
          )}
          <p>Submit this form ideally at least <strong>7 days</strong> prior to the requirement.</p>
        </TopNotice>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="fnb-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({ ...f, ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
              }));
              setParentRefCode(ref);
            }}
          />
          <ApplicantSpocSection form={form} setField={setField} />
          <SelfSkipSection form={form} setField={setField} userRole={user?.role} />
          <RequestedBySection form={form} setField={setField} depts={depts} hods={hods} subDepts={subDepts} hodOptional={isHodDropdownOptional(form)} />

          {/* Phase 9 (item 4b) — Requirement Type radio */}
          <Section numeral="II·b" title="Requirement Type">
            <Field label="Requirement Type" required>
              <div className="flex gap-3" data-testid="field-requirement_type">
                {[["food_and_beverages", "Food & Beverages"], ["cake", "Cake"]].map(([v, lbl]) => (
                  <label key={v} className={`px-4 py-2 rounded-md border cursor-pointer text-sm font-medium ${form.requirement_type === v ? "bg-[#7a1f2b] text-white border-[#5a1520]" : "bg-white text-[#7a1f2b] border-[#e2d7c3]"}`}>
                    <input type="radio" className="hidden" name="requirement_type" value={v}
                            checked={form.requirement_type === v}
                            onChange={(e) => setField("requirement_type", e.target.value)}
                            data-testid={`requirement_type-${v}`} />
                    {lbl}
                  </label>
                ))}
              </div>
            </Field>
          </Section>

          {form.requirement_type === "cake" ? (
            <Section numeral="III" title="Cake details">
              <Field label="Cake To Whom" required>
                <RadioGroup name="cake_to_whom" value={form.cake_to_whom}
                             onChange={(v) => setField("cake_to_whom", v)}
                             options={["Student", "Staff", "Guest"]} testid="field-cake_to_whom" />
              </Field>
              {form.cake_to_whom === "Student" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Student Name" required>
                    <input className="input-vgws" required value={form.cake_student_name}
                            onChange={(e) => setField("cake_student_name", e.target.value)}
                            data-testid="field-cake_student_name" />
                  </Field>
                  <Field label="Grade" required>
                    <select className="input-vgws" required value={form.cake_student_grade}
                             onChange={(e) => setField("cake_student_grade", e.target.value)}
                             data-testid="field-cake_student_grade">
                      <option value="">Select</option>
                      {["EYP 1","EYP 2","EYP 3","PYP 1","PYP 2","PYP 3","PYP 4","PYP 5",
                        "MYP 1","MYP 2","MYP 3","MYP 4","MYP 5","IBDP 1","IBDP 2","IBCP 1","IBCP 2"]
                        .map((g) => <option key={g}>{g}</option>)}
                    </select>
                  </Field>
                  <Field label="GR Number" required>
                    <input className="input-vgws" required value={form.cake_student_gr_number}
                            onChange={(e) => setField("cake_student_gr_number", e.target.value)}
                            data-testid="field-cake_student_gr_number" />
                  </Field>
                  <Field label="Day School / Boarding" required>
                    <RadioGroup name="cake_student_day_boarding" value={form.cake_student_day_boarding}
                                 onChange={(v) => setField("cake_student_day_boarding", v)}
                                 options={["Day School", "Boarding"]}
                                 testid="field-cake_student_day_boarding" />
                  </Field>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Cake Weight (kg)" required>
                  <select className="input-vgws" required value={form.cake_weight}
                           onChange={(e) => setField("cake_weight", e.target.value)}
                           data-testid="field-cake_weight">
                    <option value="">Select</option>
                    {["1","1.5","2","2.5","3","4","5"].map((w) => <option key={w}>{w}</option>)}
                  </select>
                </Field>
                <Field label="Cake Flavor" required>
                  <select className="input-vgws" required value={form.cake_flavor}
                           onChange={(e) => setField("cake_flavor", e.target.value)}
                           data-testid="field-cake_flavor">
                    <option value="">Select</option>
                    {["Chocolate","Butterscotch","Pineapple","Strawberry","Cafe Mocha"].map((f) => <option key={f}>{f}</option>)}
                  </select>
                  <div className="mt-1 text-[11px] text-red-600 leading-snug">Selected flavors provided if available in stock.</div>
                </Field>
              </div>
              <Field label="Message on Cake" hint="Optional">
                <input className="input-vgws" value={form.cake_message}
                        onChange={(e) => setField("cake_message", e.target.value)}
                        data-testid="field-cake_message" />
              </Field>
              <Field label="Cutleries Required?">
                <RadioGroup name="cutleries_required_cake" value={form.cutleries_required_cake}
                             onChange={(v) => setField("cutleries_required_cake", v)}
                             options={["Yes", "No"]} testid="field-cutleries_required_cake" />
              </Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Cake Requirement Date" required>
                  <input type="date" className="input-vgws" required value={form.cake_required_date}
                          onChange={(e) => setField("cake_required_date", e.target.value)}
                          data-testid="field-cake_required_date" />
                </Field>
                <Field label="Cake Requirement Time" required>
                  <input type="time" className="input-vgws" required value={form.cake_required_time}
                          onChange={(e) => setField("cake_required_time", e.target.value)}
                          data-testid="field-cake_required_time" />
                </Field>
                <Field label="Cake Requirement Location" required>
                  <input className="input-vgws" required value={form.cake_required_location}
                          onChange={(e) => setField("cake_required_location", e.target.value)}
                          data-testid="field-cake_required_location" />
                </Field>
              </div>
            </Section>
          ) : (
          <Section numeral="III" title="Requirement details">
            <Field label="Purpose of food & beverages requirement" required>
              <textarea className="input-vgws min-h-[70px]" required value={form.purposeOfRequirement}
                        onChange={(e) => setField("purposeOfRequirement", e.target.value)}
                        data-testid="field-purposeOfRequirement" />
            </Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="How many people" required>
                <input type="number" min="1" className="input-vgws" value={form.peopleCount}
                       onChange={(e) => setField("peopleCount", e.target.value)}
                       data-testid="field-peopleCount" />
              </Field>
              <Field label="Requirement date" required>
                <input type="date" className="input-vgws" value={form.requirementDate}
                       onChange={(e) => setField("requirementDate", e.target.value)}
                       data-testid="field-requirementDate" />
              </Field>
              <Field label="Requirement time" required>
                <input type="time" className="input-vgws" value={form.requirementTime}
                       onChange={(e) => setField("requirementTime", e.target.value)}
                       data-testid="field-requirementTime" />
              </Field>
            </div>
            <Field label="Location — mention clearly in detail" required>
              <textarea className="input-vgws min-h-[60px]" value={form.location}
                        onChange={(e) => setField("location", e.target.value)}
                        data-testid="field-location" />
            </Field>
            {/* Phase 9 (item 4d) — student count fields for F&B */}
            <Field label="Students Included?">
              <RadioGroup name="students_included" value={form.students_included}
                           onChange={(v) => setField("students_included", v)}
                           options={["Yes", "No"]} testid="field-students_included" />
            </Field>
            {form.students_included === "Yes" && (
              <>
                <Field label="Student Type" required>
                  <RadioGroup name="student_type" value={form.student_type}
                               onChange={(v) => setField("student_type", v)}
                               options={["Day School", "Boarding", "Both"]} testid="field-student_type" />
                </Field>
                <div className="grid sm:grid-cols-3 gap-4">
                  {(form.student_type === "Day School" || form.student_type === "Both") && (
                    <Field label="Day School Count" required>
                      <input type="number" min="0" className="input-vgws" value={form.day_school_count}
                              onChange={(e) => setField("day_school_count", e.target.value)}
                              data-testid="field-day_school_count" />
                    </Field>
                  )}
                  {(form.student_type === "Boarding" || form.student_type === "Both") && (
                    <Field label="Boarding Count" required>
                      <input type="number" min="0" className="input-vgws" value={form.boarding_count}
                              onChange={(e) => setField("boarding_count", e.target.value)}
                              data-testid="field-boarding_count" />
                    </Field>
                  )}
                  {form.student_type === "Both" && (
                    <Field label="Total Students Count" required>
                      <input type="number" min="0" className="input-vgws" value={form.total_students_count}
                              onChange={(e) => setField("total_students_count", e.target.value)}
                              data-testid="field-total_students_count" />
                    </Field>
                  )}
                </div>
              </>
            )}
          </Section>
          )}
          {form.requirement_type !== "cake" && (
          <Section numeral="IV" title="Food & beverage selection">
            <Field label="Food & beverage service to whom" required hint="Select at least one">
              <CheckboxGroup value={form.serviceTo} onChange={(v) => setField("serviceTo", v)}
                             options={SERVICE_TO} testid="field-serviceTo" />
            </Field>
            <Field label="Select items required" required hint="Select at least one">
              <CheckboxGroup value={form.itemsSelected} onChange={(v) => setField("itemsSelected", v)}
                             options={ITEMS} testid="field-itemsSelected" />
            </Field>
            {form.itemsSelected.includes("Other") && (
              <Field label="Please specify the other item required" required>
                <input className="input-vgws" value={form.otherItem}
                       onChange={(e) => setField("otherItem", e.target.value)}
                       data-testid="field-otherItem" />
              </Field>
            )}
            <FileField label="Attach menu for specific requirements" fieldName="menu"
                       accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                       attachments={attachments} onChange={(e) => onUpload("menu", e)}
                       disabled={uploading} testid="attach-menu" optional />
          </Section>
          )}
          {/* Phase 9 (item 4e) — Payment section (both cake + F&B) */}
          <Section numeral="V·payment" title="Payment details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Payment Paid By" required>
                <select className="input-vgws" required value={form.payment_paid_by}
                         onChange={(e) => setField("payment_paid_by", e.target.value)}
                         data-testid="field-payment_paid_by">
                  <option value="">Select</option>
                  <option value="school">School</option>
                  <option value="student_imprest">Student Imprest</option>
                  <option value="staff">Staff</option>
                  <option value="parent">Parents</option>
                </select>
              </Field>
              <Field label="Payment Mode" required>
                <select className="input-vgws" required value={form.payment_mode}
                         onChange={(e) => setField("payment_mode", e.target.value)}
                         data-testid="field-payment_mode">
                  <option value="">Select</option>
                  <option>Deduct from Student Imprest</option>
                  <option>Payment Link</option>
                  <option>UPI Scanner</option>
                  <option>Bank Transfer</option>
                  {/* Phase 18 (item 2) — "Paid by school" only on the Cake path. */}
                  {form.requirement_type === "cake" && (
                    <option>Paid by school</option>
                  )}
                </select>
              </Field>
            </div>
            {String(form.payment_paid_by || "").toLowerCase() === "parent" && (
              <div className="grid sm:grid-cols-2 gap-4 mt-2 p-4 rounded-md border border-[#b48a45] bg-[#faf5eb]">
                <Field label="Parent Email" required>
                  <input type="email" className="input-vgws" required value={form.parent_email}
                          onChange={(e) => setField("parent_email", e.target.value)}
                          data-testid="field-parent_email" />
                </Field>
                <div className="flex items-end">
                  <button type="button" className="btn-vgws"
                           onClick={() => toast.info("Payment details will be sent automatically on submit.")}
                           data-testid="btn-send-parent-payment">
                    Send Payment Details to Parent
                  </button>
                </div>
              </div>
            )}
            <FileField label="Payment Done File" fieldName="payment_done_file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        attachments={attachments} onChange={(e) => onUpload("payment_done_file", e)}
                        disabled={uploading} testid="attach-payment_done_file" optional />
          </Section>

          {/* Phase 9 (item 4f) — Single mandatory consent */}
          <Section numeral="V·consent" title="Applicant consent" testid="section-fnb-consent">
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-[#faf5eb]">
              <input type="checkbox"
                      className="mt-0.5 w-4 h-4 accent-[#7a1f2b] shrink-0"
                      checked={!!form.consents.applicantResponsibleInfo}
                      onChange={(e) => setForm((f) => ({ ...f, consents: { ...f.consents, applicantResponsibleInfo: e.target.checked } }))}
                      data-testid="consent-applicantResponsibleInfo" />
              <span className="text-[#1f2320]">Applicant is responsible to provide the correct information. <span className="text-[#b3261e]">*</span></span>
            </label>
          </Section>

          {form.requirement_type !== "cake" && (
          <>
          <Section numeral="V" title="Special instructions">
            <Field label="Specific cooking instructions">
              <textarea className="input-vgws min-h-[70px]" value={form.cookingInstructions}
                        onChange={(e) => setField("cookingInstructions", e.target.value)}
                        data-testid="field-cookingInstructions" />
            </Field>
            <Field label="Food allergies, if any, particularly">
              <textarea className="input-vgws min-h-[60px]" value={form.allergies}
                        onChange={(e) => setField("allergies", e.target.value)}
                        data-testid="field-allergies" />
            </Field>
          </Section>

          <Section numeral="VI" title="Service arrangements">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Serving cutleries required?" required>
                <RadioGroup name="cutleriesRequired" value={form.cutleriesRequired}
                            onChange={(v) => setField("cutleriesRequired", v)}
                            options={["Yes", "No"]} testid="field-cutleriesRequired" />
              </Field>
              <Field label="Serving staff required from refectory?" required>
                <RadioGroup name="servingStaffRequired" value={form.servingStaffRequired}
                            onChange={(v) => setField("servingStaffRequired", v)}
                            options={["Yes", "No"]} testid="field-servingStaffRequired" />
              </Field>
            </div>
          </Section>
          </>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required.
            </p>
            <button type="submit" disabled={submitting} className="btn-vgws" data-testid="submit-fnb-button">
              <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
